import { FirstStepSignupAssignment, FirstStepSignupConfig } from '../types/workshopFirstStepSignup';

export function totalSignupColumns(config: FirstStepSignupConfig): number {
  return config.standard_slot_count + config.alternate_slot_count;
}

export function columnHeaders(config: FirstStepSignupConfig): string[] {
  const std = Array.from({ length: config.standard_slot_count }, () => 'Presenter');
  const alt = Array.from({ length: config.alternate_slot_count }, () => 'Alternate');
  return [...std, ...alt];
}

export function assignmentAt(
  config: FirstStepSignupConfig,
  weekIndex: number,
  columnIndex: number
): FirstStepSignupAssignment | undefined {
  return config.assignments.find(
    (a) => a.week_index === weekIndex && a.column_index === columnIndex
  );
}

export function myAssignment(
  config: FirstStepSignupConfig,
  viewerUserId?: string
): FirstStepSignupAssignment | undefined {
  return config.assignments.find(
    (a) => a.is_you || (viewerUserId != null && a.user_id === viewerUserId)
  );
}

export function weekPresentationDate(
  config: FirstStepSignupConfig,
  weekIndex: number
): string | null {
  const weekMeta = config.weeks?.find((w) => w.week_index === weekIndex);
  if (weekMeta?.presentation_date) return weekMeta.presentation_date;

  const first = (config.first_presentation_week_date || '').trim();
  if (!first || weekIndex < 1) return null;
  const [y, m, d] = first.split('-').map(Number);
  if (!y || !m || !d) return null;
  const start = new Date(y, m - 1, d);
  if (Number.isNaN(start.getTime())) return null;
  start.setDate(start.getDate() + 7 * (weekIndex - 1));
  const yy = start.getFullYear();
  const mm = String(start.getMonth() + 1).padStart(2, '0');
  const dd = String(start.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function formatPresentationDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function weekLockedForMember(
  config: FirstStepSignupConfig,
  weekIndex: number
): boolean {
  const week = config.weeks?.find((w) => w.week_index === weekIndex);
  if (week) return week.locked_for_member;
  if (config.locked_week_indexes.includes(weekIndex)) return true;
  return false;
}

export function isAlternateSlot(config: FirstStepSignupConfig, columnIndex: number): boolean {
  return columnIndex >= config.standard_slot_count;
}

export function firstStepSignupCollapsedSummary(
  config: FirstStepSignupConfig | null,
  loading: boolean,
  viewerUserId?: string
): string {
  if (loading) return 'Loading…';
  if (!config) return 'Not open yet';
  if (!config.enabled && config.assignments.length === 0) return 'Not open yet';

  const mine = myAssignment(config, viewerUserId);
  if (!mine) return 'Not signed up yet';

  const role = isAlternateSlot(config, mine.column_index) ? 'alternate' : 'presenter';
  const date = formatPresentationDate(weekPresentationDate(config, mine.week_index));
  if (date) {
    return `Signed up as ${role} for week ${mine.week_index} — ${date}`;
  }
  return `Signed up as ${role} for week ${mine.week_index}`;
}

export function firstStepSignupShouldShow(
  config: FirstStepSignupConfig | null,
  _options?: { canFacilitate?: boolean }
): boolean {
  return !!config?.enabled;
}

export function firstStepSignupStatusLine(
  config: FirstStepSignupConfig,
  viewerUserId?: string
): string | null {
  const mine = myAssignment(config, viewerUserId);
  if (!mine) return null;
  const role = isAlternateSlot(config, mine.column_index) ? 'an alternate' : 'a presenter';
  return `You are signed up as ${role} for week ${mine.week_index}.`;
}
